import { useEffect, useRef } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { toast } from 'sonner';
import { graphql } from '@/gql';
import type { JobKind } from '@/gql/graphql';
import { saveErrorMessage } from '@/shared/lib/graphql-errors';

export const ADMIN_JOB_RUN = graphql(`
  query AdminJobRun($kind: JobKind!) {
    adminJobRun(kind: $kind) {
      kind
      status
      startedAt
      finishedAt
      error
      attempts
    }
  }
`);

export const START_JOB = graphql(`
  mutation StartJob($kind: JobKind!) {
    startJob(kind: $kind) {
      kind
      status
      startedAt
      finishedAt
      error
      attempts
    }
  }
`);

const POLL_MS = 3000;

/**
 * `startJob` returns while the work runs, so the run is polled until it leaves RUNNING and the
 * outcome is toasted from the ledger. A job already running when the page opens is followed too.
 */
export const useJob = (
  kind: JobKind,
  messages: { succeeded: string; failed: string; onSucceeded?: () => void },
) => {
  const run = useQuery(ADMIN_JOB_RUN, { variables: { kind }, fetchPolicy: 'network-only' });
  const status = run.data?.adminJobRun?.status;
  const error = run.data?.adminJobRun?.error ?? null;
  const isRunning = status === 'RUNNING';
  const wasRunning = useRef(false);
  const { startPolling, stopPolling, refetch } = run;
  const onSucceeded = useRef(messages.onSucceeded);
  onSucceeded.current = messages.onSucceeded;

  const [start, { loading }] = useMutation(START_JOB, {
    variables: { kind },
    onCompleted: () => void refetch(),
    onError: (err) => toast.error(saveErrorMessage(err, messages.failed)),
  });

  useEffect(() => {
    if (isRunning) {
      wasRunning.current = true;
      startPolling(POLL_MS);
      return stopPolling;
    }
    if (!wasRunning.current || !status) return;
    wasRunning.current = false;
    if (status === 'FAILED') {
      toast.error(`${messages.failed} ${error ?? ''}`.trim());
    } else {
      toast.success(messages.succeeded);
      onSucceeded.current?.();
    }
  }, [isRunning, status, error, startPolling, stopPolling, messages.failed, messages.succeeded]);

  return { start: () => start(), run: run.data?.adminJobRun ?? null, isRunning: loading || isRunning };
};
